// @story: libs/storybook/stories/modules/auth/directory-teams-defaults.stories.ts
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Dialog } from '@angular/cdk/dialog';
import { DirectorySettingsService, type CalendarSlotCustomPropertyDefinition, type DirectoryConfig } from '../directory-settings.service';
import {
  AccordionComponent,
  AccordionItemComponent,
  ConfirmDialogService,
  type DropdownOption,
  Dropdown,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  FormField,
  HelpDialogService,
  Input,
  LanguageService,
  LoadingStateComponent,
  MultiSelect,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  ToastService,
  TooltipDirective,
  ZagButton,
  ZagIcon,
  ZagSwitch,
} from '@zag/ui';
import { OutlookCalendarAssignmentDialogComponent } from '@zag/outlook/ui';
import { FeatureAccessClientService } from '@zag/features/ui';
import { PermissionService } from '../permission.service';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, catchError, of } from 'rxjs';

function recipientIconForDefinition(definition: CalendarSlotCustomPropertyDefinition): string {
  if (definition.propertyType === 'select') return 'list';
  const inputType = definition.config?.['inputType'];
  if (inputType === 'email') return 'mail';
  if (inputType === 'tel') return 'call';
  return 'text_fields';
}

const RECIPIENT_PATH_SOURCE_KEYS = {
  calendarSlot: 'settings.customPropertyDefinitions.entityTypes.calendarSlot',
  customProperty: 'settings.directory.microsoft.teams.sourceCustomProperty',
} as const;

function recipientPathSource(path: string): keyof typeof RECIPIENT_PATH_SOURCE_KEYS | null {
  if (path.startsWith('data.customProperties.')) return 'customProperty';
  if (path.startsWith('data.')) return 'calendarSlot';
  return null;
}

@Component({
  selector: 'sc-directory-settings',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    AccordionComponent,
    AccordionItemComponent,
    FormField,
    Dropdown,
    Input,
    MultiSelect,
    ZagButton,
    ZagIcon,
    ZagSwitch,
    TooltipDirective,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './directory-settings.component.html',
  styleUrl: './directory-settings.component.scss',
})
export class DirectorySettingsComponent {
  private readonly fb = inject(FormBuilder);
  private readonly directorySettings = inject(DirectorySettingsService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly helpDialog = inject(HelpDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  private readonly languageService = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly permissions = inject(PermissionService);
  private readonly featureAccess = inject(FeatureAccessClientService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly isSaving = signal(false);
  protected readonly isTesting = signal(false);
  protected readonly testResult = signal<{ success: boolean; message: string } | null>(null);
  protected readonly secretPlaceholder = signal('');

  protected readonly profileName = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.directory.title');
  });
  protected readonly profileSubtitle = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.directory.description');
  });

  protected readonly settingsForm = this.fb.group({
    microsoftEnabled: [false],
    microsoftTenantId: [''],
    microsoftClientId: [''],
    microsoftClientSecret: [''],
    teamsEnabled: [false],
    teamsRecipientPaths: [[] as string[]],
    teamsTemplateKey: [null as string | null],
  });

  private readonly currentRecipientPaths = toSignal(this.settingsForm.controls.teamsRecipientPaths.valueChanges, {
    initialValue: [] as string[],
  });

  private readonly currentTemplateKey = toSignal(this.settingsForm.controls.teamsTemplateKey.valueChanges, {
    initialValue: null as string | null,
  });

  private readonly emailCapableDefinitionsResource = rxResource<CalendarSlotCustomPropertyDefinition[], { lang: string }>({
    params: () => ({ lang: this.languageService.currentLanguage() }),
    stream: () =>
      this.directorySettings.getCalendarSlotCustomPropertyDefinitions().pipe(
        map(definitions => definitions.filter(definition => definition.propertyType === 'text' || definition.propertyType === 'select')),
        catchError(() => of([] as CalendarSlotCustomPropertyDefinition[]))
      ),
  });

  protected readonly recipientPathOptions = computed<DropdownOption<string>[]>(() => {
    this.currentLang();
    const staticOptions: DropdownOption<string>[] = [
      {
        value: 'data.customer.email',
        label: this.translate.instant('settings.directory.microsoft.teams.pathCustomerEmail'),
        icon: 'person',
      },
      {
        value: 'data.advisor.email',
        label: this.translate.instant('settings.directory.microsoft.teams.pathAdvisorEmail'),
        icon: 'person',
      },
    ];
    if (this.emailCapableDefinitionsResource.isLoading()) return staticOptions;
    const dynamic = (this.emailCapableDefinitionsResource.value() ?? []).map(definition => ({
      value: `data.customProperties.${definition.key}`,
      label: definition.label,
      icon: recipientIconForDefinition(definition),
    }));
    const base = [...staticOptions, ...dynamic];
    const known = new Set(base.map(option => option.value));
    const unknowns = (this.currentRecipientPaths() ?? []).filter(
      (value): value is string => typeof value === 'string' && !known.has(value)
    );
    return [...base, ...unknowns.map(value => ({ value, label: value, icon: 'help' }))].map(option => ({
      ...option,
      badge: this.recipientPathBadge(option.value),
    }));
  });

  private recipientPathBadge(path: string): string | undefined {
    const source = recipientPathSource(path);
    return source ? this.translate.instant(RECIPIENT_PATH_SOURCE_KEYS[source]) : undefined;
  }

  private readonly emailTemplateOptionsResource = rxResource<DropdownOption<string>[], void>({
    stream: () => this.directorySettings.getEmailTemplateOptions().pipe(catchError(() => of([] as DropdownOption<string>[]))),
  });

  protected readonly templateKeyOptions = computed<DropdownOption<string>[]>(() => {
    const base = this.emailTemplateOptionsResource.value() ?? [];
    const current = this.currentTemplateKey();
    const fallback: DropdownOption<string>[] =
      typeof current === 'string' && current.length > 0 && !base.some(option => option.value === current)
        ? [{ value: current, label: current }]
        : [];
    return [...base, ...fallback];
  });

  constructor() {
    const credentialControls = ['microsoftTenantId', 'microsoftClientId', 'microsoftClientSecret'] as const;
    for (const name of credentialControls) {
      this.settingsForm.controls[name].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
        if (typeof value !== 'string' || value.trim().length === 0) return;
        if (this.settingsForm.controls['microsoftEnabled'].value) return;
        this.settingsForm.controls['microsoftEnabled'].setValue(true, { emitEvent: false });
        this.applyMicrosoftValidators(true);
      });
    }
  }

  protected readonly configResource = rxResource<DirectoryConfig | null, void>({
    stream: () =>
      this.directorySettings.getConfig().pipe(
        map(response => {
          this.populateForm(response);
          return response;
        }),
        catchError(() => {
          this.toast.error(this.translate.instant('settings.directory.loadError'));
          return of(null);
        })
      ),
  });

  private populateForm(config: DirectoryConfig): void {
    const ms = config.microsoft;
    const teams = config.teamsInvitationDefaults;
    this.settingsForm.patchValue({
      microsoftEnabled: ms?.enabled ?? false,
      microsoftTenantId: ms?.tenantId ?? '',
      microsoftClientId: ms?.clientId ?? '',
      microsoftClientSecret: '',
      teamsEnabled: teams?.enabled ?? false,
      teamsRecipientPaths: teams?.recipientPropertyKeys ?? [],
      teamsTemplateKey: teams?.confirmationTemplateKey ?? null,
    });
    this.secretPlaceholder.set((ms as { hasClientSecret?: boolean } | null)?.hasClientSecret ? '••••••••' : '');
    this.applyMicrosoftEnabledState(ms?.enabled ?? false);
    this.settingsForm.markAsPristine();
  }

  protected onSave(): void {
    this.saveConfig();
  }

  private isTeamsSectionDirty(): boolean {
    const controls = this.settingsForm.controls;
    return controls['teamsEnabled'].dirty || controls['teamsRecipientPaths'].dirty || controls['teamsTemplateKey'].dirty;
  }

  private buildTeamsInvitationDefaults(): DirectoryConfig['teamsInvitationDefaults'] {
    const v = this.settingsForm.value;
    const templateKey = (v.teamsTemplateKey ?? '').trim().toLowerCase();
    return {
      enabled: v.teamsEnabled ?? false,
      recipientPropertyKeys: v.teamsRecipientPaths ?? [],
      confirmationTemplateKey: templateKey ? templateKey : null,
    };
  }

  private saveConfig(): Promise<boolean> {
    if (this.settingsForm.invalid) {
      this.settingsForm.markAllAsTouched();
      return Promise.resolve(false);
    }

    return new Promise<boolean>(resolve => {
      this.isSaving.set(true);

      const v = this.settingsForm.value;
      const newSecret = v.microsoftClientSecret?.trim() || null;

      const enabled = v.microsoftEnabled ?? false;
      const payload: DirectoryConfig = {
        microsoft: {
          enabled,
          tenantId: enabled ? v.microsoftTenantId || undefined : undefined,
          clientId: enabled ? v.microsoftClientId || undefined : undefined,
          ...(enabled && newSecret ? { clientSecret: newSecret } : {}),
        },
        ...(this.isTeamsSectionDirty() ? { teamsInvitationDefaults: this.buildTeamsInvitationDefaults() } : {}),
      };

      if (!enabled) {
        this.settingsForm.patchValue({ microsoftTenantId: '', microsoftClientId: '', microsoftClientSecret: '' }, { emitEvent: false });
      }

      this.directorySettings
        .patchConfig(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.isSaving.set(false);
            this.toast.success(this.translate.instant('settings.directory.saveSuccess'));
            this.populateForm(response);
            this.settingsForm.markAsPristine();
            resolve(true);
          },
          error: () => {
            this.isSaving.set(false);
            this.toast.error(this.translate.instant('settings.directory.saveError'));
            resolve(false);
          },
        });
    });
  }

  protected async onMicrosoftEnabledToggle(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const value = target.checked;

    if (!value && this.hasConfigValues()) {
      const confirmed = await this.confirmDisable();
      if (!confirmed) {
        target.checked = true;
        return;
      }
    }

    this.settingsForm.controls['microsoftEnabled'].setValue(value);
    this.applyMicrosoftEnabledState(value);
  }

  protected onTeamsEnabledChange(enabled: boolean): void {
    const control = this.settingsForm.controls['teamsEnabled'];
    control.setValue(enabled);
    control.markAsDirty();
  }

  private hasConfigValues(): boolean {
    const v = this.settingsForm.getRawValue();
    return !!(v.microsoftTenantId?.trim() || v.microsoftClientId?.trim() || v.microsoftClientSecret?.trim());
  }

  private applyMicrosoftEnabledState(enabled: boolean): void {
    this.applyMicrosoftValidators(enabled);
  }

  private applyMicrosoftValidators(enabled: boolean): void {
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const tenantId = this.settingsForm.controls['microsoftTenantId'];
    const clientId = this.settingsForm.controls['microsoftClientId'];
    const clientSecret = this.settingsForm.controls['microsoftClientSecret'];

    if (enabled) {
      tenantId.setValidators([Validators.required, Validators.pattern(uuidPattern)]);
      clientId.setValidators([Validators.required, Validators.pattern(uuidPattern)]);
      const hasStoredSecret = this.secretPlaceholder().length > 0;
      clientSecret.setValidators(hasStoredSecret ? [] : [Validators.required]);
    } else {
      tenantId.clearValidators();
      clientId.clearValidators();
      clientSecret.clearValidators();
    }
    tenantId.updateValueAndValidity({ emitEvent: false });
    clientId.updateValueAndValidity({ emitEvent: false });
    clientSecret.updateValueAndValidity({ emitEvent: false });
  }

  protected getFieldError(fieldName: string): string | null {
    const control = this.settingsForm.get(fieldName);
    if (!control || !control.errors || (!control.touched && !control.dirty)) return null;

    if (control.errors['required']) {
      return this.translate.instant('settings.directory.errors.required');
    }
    if (control.errors['pattern']) {
      return this.translate.instant('settings.directory.errors.invalidUuid');
    }
    return null;
  }

  private confirmDisable(): Promise<boolean> {
    return this.confirmDialog.confirm({
      title: 'settings.directory.disableDialog.title',
      message: 'settings.directory.disableDialog.message',
      confirmText: 'settings.directory.disableDialog.confirm',
      destructive: true,
    });
  }

  protected readonly canManageCalendarAssignments = computed(
    () => this.featureAccess.hasFeature('outlook') && this.permissions.hasPermission('calendars:update')
  );

  protected openCalendarAssignmentDialog(): void {
    this.dialog.open(OutlookCalendarAssignmentDialogComponent, {
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      width: 'min(96vw, 1100px)',
      maxWidth: '96vw',
    });
  }

  protected openMicrosoftHelp(): void {
    this.helpDialog.openTopic('directory.microsoft');
  }

  protected openGoogleHelp(): void {
    this.helpDialog.openTopic('directory.google');
  }

  protected async onTestConnection(): Promise<void> {
    if (this.settingsForm.dirty) {
      const confirmed = await this.confirmSaveBeforeTest();
      if (!confirmed) return;
      const saved = await this.saveConfig();
      if (!saved) return;
    }
    this.runTestConnection();
  }

  private runTestConnection(): void {
    this.isTesting.set(true);
    this.testResult.set(null);
    this.directorySettings
      .testConnection({ provider: 'microsoft' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isTesting.set(false);
          this.testResult.set({
            success: result.success,
            message: result.success
              ? this.translate.instant('settings.directory.test.success')
              : (result.message ?? this.translate.instant('settings.directory.test.error')),
          });
        },
        error: error => {
          this.isTesting.set(false);
          const message = (error?.error?.message as string | undefined) ?? this.translate.instant('settings.directory.test.error');
          this.testResult.set({ success: false, message });
        },
      });
  }

  private confirmSaveBeforeTest(): Promise<boolean> {
    return this.confirmDialog.confirm({
      title: 'settings.directory.testDialog.title',
      message: 'settings.directory.testDialog.message',
      confirmText: 'settings.directory.testDialog.confirm',
    });
  }
}
